import React from "react";
import cn from "classnames";
import styles from "./gateways-list-row.module.scss";

import { IconButton } from "@components/atoms/buttons/icon-button/icon-button";
import arrow from "@src/assets/svg/arrow.svg";
import { SourcesListForGatewaysList } from "@src/components/organisms/sources-list-for-gateways-list/sources-list-for-gateways-list";
import { SourcesListHeaderForGatewaysList } from "../sources-list-header-for-gateways-list/sources-list-header-for-gateways-list";
import gatewayIcon from "@src/assets/svg/gateway-icon.svg";
import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { useGatewaysListRowHook } from "./use-gateways-list-row-hook";
import { EditGatewayName } from "../edit-gateway-name/edit-gateway-name";
import { Button } from "@src/components/atoms/buttons/button/button";
import { useI18N } from "@src/language/language-context";
import { RenderIfUserHasMultiplePermissions } from "../render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";

interface GatewaysListRowPropTypes {
  gatewayData: GatewayWithSourceViewStateTypes;
}

const cssClassName = "gateways-list-row";

export const GatewaysListRow = ({ gatewayData }: GatewaysListRowPropTypes) => {
  const targetGatewayUuid = new URLSearchParams(window.location.search).get(
    "gateway_uuid",
  );
  const { toggleSourcesVisibility, handleAddMachineClick } =
    useGatewaysListRowHook(gatewayData);
  const i18n = useI18N();

  const texts = {
    gatewayIconAltText: i18n("modules.gatewaysListRow.gatewayIconAltText"),
    addMachineText: i18n("modules.gatewaysListRow.addMachineText"),
    addMachineTooltip: i18n("modules.gatewaysListRow.addMachineTooltip"),
    noVendorIDAvailable: i18n("modules.gatewaysListRow.noVendorIDAvailable"),
  };

  return (
    <tr
      key={gatewayData.gateway_uuid}
      id={`gateway-row-${gatewayData.gateway_uuid}`}
      className={cn(styles[`${cssClassName}__grid-row`], {
        [styles[`${cssClassName}__grid-row--open`]]:
          gatewayData.sources_visible && gatewayData.sources.length > 0,
        [styles[`${cssClassName}__grid-row--selected`]]:
          targetGatewayUuid === gatewayData.gateway_uuid,
      })}
      tabIndex={0}
      role="button"
    >
      <td
        className={cn(styles[`${cssClassName}__grid-cell`], {
          [styles[`${cssClassName}__grid-cell--open`]]:
            gatewayData.sources_visible && gatewayData.sources.length > 0,
        })}
      >
        <span
          className={cn(
            styles[`${cssClassName}__grid-cell-flex-wrapper`],
            styles[
              `${cssClassName}__grid-cell-flex-wrapper--gateway-name-container`
            ],
          )}
        >
          <span
            className={styles[`${cssClassName}__indicator-padding-wrapper`]}
          >
            {/* <IndicatorText text={""} variant={"green"} /> */}
          </span>
          <img src={gatewayIcon} alt={texts.gatewayIconAltText} />

          <EditGatewayName gateway={gatewayData} />

          <RenderIfUserHasMultiplePermissions
            permissions={["manage-gateways", "manage-sources"]}
            requireAll
          >
            <span className={styles[`${cssClassName}__no-shrink-wrapper`]}>
              <VerticalSeparator />
            </span>
            <span className={styles[`${cssClassName}__add-source-wrapper`]}>
              <Button
                fun={handleAddMachineClick}
                text={texts.addMachineText}
                variant="uppercase"
                tooltip={texts.addMachineTooltip}
              />
            </span>
          </RenderIfUserHasMultiplePermissions>

          <span
            className={cn(
              styles[`${cssClassName}__grid-cell-flex-wrapper`],
              styles[`${cssClassName}__grid-cell-flex-wrapper--no-gap`],
            )}
          >
            <span
              className={cn(styles[`${cssClassName}__sources-amount-text`])}
            >
              {gatewayData.sources.length}
            </span>
            {gatewayData.sources.length > 0 && (
              <span className={styles[`${cssClassName}__no-shrink-wrapper`]}>
                <IconButton
                  icon={arrow}
                  fun={toggleSourcesVisibility}
                  rotate={gatewayData.sources_visible}
                />
              </span>
            )}
          </span>
        </span>
      </td>
      <td
        className={cn(styles[`${cssClassName}__grid-cell`], {
          [styles[`${cssClassName}__grid-cell--open`]]:
            gatewayData.sources_visible && gatewayData.sources.length > 0,
        })}
      >
        <span className={styles[`${cssClassName}__description-text`]}>
          {gatewayData.vendor_id
            ? gatewayData.vendor_id
            : texts.noVendorIDAvailable}
        </span>
      </td>
      <td
        className={cn(styles[`${cssClassName}__grid-cell`], {
          [styles[`${cssClassName}__grid-cell--open`]]:
            gatewayData.sources_visible && gatewayData.sources.length > 0,
        })}
      >
        <span className={styles[`${cssClassName}__description-text`]}>
          {gatewayData.gateway_uuid}
        </span>
      </td>
      {gatewayData.sources.length > 0 && gatewayData.sources_visible && (
        <td className={styles[`${cssClassName}__sources-table-container`]}>
          <span
            className={styles[`${cssClassName}__sources-table-outer-wrapper`]}
          >
            <table className={styles[`${cssClassName}__table`]}>
              <SourcesListHeaderForGatewaysList />
              <SourcesListForGatewaysList gatewayData={gatewayData} />
            </table>
          </span>
        </td>
      )}
    </tr>
  );
};
